import { useMemo, useState } from "react";
import { api, useLoad } from "../api";
import { fmt } from "../lib/format";
import { linkHandler } from "../lib/router";
import { Hint } from "../components/Hint";
import { UsRulesSwitch, usd, useUsRules } from "../components/UsRules";
import type { UsActiveTrade } from "../types";

type Side = "all" | "buy" | "sell";

/**
 * Активные сделки США (ТЗ США, задача 4): открытые позиции по выбранным правилам на последнем дне цен, сетапы, по
 * которым вход завтра, и сделки, закрытые за последние 6 недель. Дата входа — первый торговый день после публикации
 * отчёта CFTC; стоп — в цене и в долларах на один контракт.
 */
export function UsTradesPage() {
  const [rules, setRules] = useUsRules();
  const { data, error, loading } = useLoad((s) => api.usTrades(rules, s), [rules]);
  const [side, setSide] = useState<Side>("all");
  const open = data?.open ?? [];
  const rows = useMemo(() => open.filter((t) => side === "all" || (side === "buy" ? t.dir > 0 : t.dir < 0)), [open, side]);
  const total = open.reduce((a, t) => a + t.pnlUsd, 0);

  return (
    <main className="page journal-page trades-page us">
      <a className="back" href="/us" onClick={linkHandler("/us")}>
        ← Рынки США
      </a>
      <h1>Сделки США</h1>
      <p className="lede">
        Открытые позиции по правилам бэктеста США на закрытие {data?.lastPriceDate ? fmt.date(data.lastPriceDate) : "последнего дня цен"}.
        Отчёт CFTC — позиции на вторник, публикация обычно в пятницу в 15:30 по Нью-Йорку; вход — открытие следующего
        торгового дня. Деньги — на один контракт.
      </p>
      <UsRulesSwitch rules={rules} onChange={setRules} />
      {data ? <p className="explain">{data.note}</p> : null}
      {error ? <p className="form-error" role="alert">Не удалось загрузить: {error}</p> : null}
      {loading && !data ? <div className="skeleton" aria-busy="true">Считаем сделки…</div> : null}

      {data ? (
        <>
          <section>
            <h2>
              Открытые позиции: {open.length}
              {open.length ? <span className="muted"> · итого по закрытию {usd(total)}</span> : null}
            </h2>
            <div className="list-tools">
              <div className="segmented" role="group" aria-label="Сторона">
                {(
                  [
                    ["all", "Все"],
                    ["buy", "Покупки"],
                    ["sell", "Продажи"],
                  ] as [Side, string][]
                ).map(([v, t]) => (
                  <button key={v} type="button" className={side === v ? "active" : undefined} aria-pressed={side === v} onClick={() => setSide(v)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            {rows.length === 0 ? (
              <p className="muted">Открытых позиций нет.</p>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="text">Рынок</th>
                      <th className="text">Сторона</th>
                      <th className="text">
                        Вход <Hint>Дата и цена входа: открытие первого торгового дня после публикации отчёта CFTC.</Hint>
                      </th>
                      <th className="text">
                        Отчёт <Hint>Дата отчёта CFTC (позиции на вторник), по которому открыта сделка, и дата его публикации. «Перевход» — повторный вход по максимуму 13 дней после выбивания стопом (гл. 12).</Hint>
                      </th>
                      <th>Закрытие</th>
                      <th>
                        Стоп <Hint>Действующий защитный стоп в цене. «17 дн.» — стоп уже подтянут к минимуму (максимуму) 17 дней без inside-дней.</Hint>
                      </th>
                      <th>
                        До стопа, $ <Hint>Сколько долларов на один контракт отделяет последнее закрытие от стопа.</Hint>
                      </th>
                      <th>
                        Риск входа, $ <Hint>Начальный риск: от цены входа до первого стопа, долларов на контракт.</Hint>
                      </th>
                      <th>
                        Результат <Hint>Результат по последнему закрытию, долларов на контракт, за вычетом комиссии и проскальзывания входа; в скобках — в процентах цены.</Hint>
                      </th>
                      <th>Дней</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <Row key={t.code} t={t} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2>
              Вход завтра <Hint>Сетап появился в последнем опубликованном отчёте, а входа ещё не было: вход — на открытии следующего торгового дня после последнего дня цен.</Hint>
            </h2>
            {data.pending.length === 0 ? (
              <p className="muted">Новых сетапов в последнем отчёте нет.</p>
            ) : (
              <ul className="plain-list">
                {data.pending.map((p) => (
                  <li key={p.code}>
                    <a href={`/us/${p.code}`} onClick={linkHandler(`/us/${p.code}`)}>
                      <b>{p.code}</b> {p.name}
                    </a>{" "}
                    — <span className={p.dir > 0 ? "up" : "down"}>{p.dir > 0 ? "покупка" : "продажа"}</span>, отчёт {fmt.date(p.report)}, опубликован{" "}
                    {fmt.date(p.published)}: {p.note}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2>Закрытые за 6 недель</h2>
            {data.recent.length === 0 ? (
              <p className="muted">Закрытых сделок за 6 недель нет.</p>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="text">Рынок</th>
                      <th className="text">Сторона</th>
                      <th className="text">Вход</th>
                      <th className="text">Выход</th>
                      <th className="text">Причина</th>
                      <th>Итог, $ <Hint>После комиссии и проскальзывания, на один контракт.</Hint></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((t, i) => (
                      <tr key={`${t.code}-${t.entryDate}-${i}`}>
                        <td className="text">
                          <a href={`/us/${t.code}`} onClick={linkHandler(`/us/${t.code}`)}>{t.code}</a>
                        </td>
                        <td className={`text ${t.dir > 0 ? "up" : "down"}`}>{t.dir > 0 ? "покупка" : "продажа"}</td>
                        <td className="text">{fmt.date(t.entryDate)} · {fmt.price(t.entryPrice)}</td>
                        <td className="text">{fmt.date(t.exitDate)} · {fmt.price(t.exitPrice)}</td>
                        <td className="text">{t.reason}</td>
                        <td className={t.netUsd > 0 ? "up" : t.netUsd < 0 ? "down" : undefined}>{usd(t.netUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}

function Row({ t }: { t: UsActiveTrade }) {
  const to = `/us/${encodeURIComponent(t.code)}`;
  return (
    <tr>
      <td className="text">
        <a href={to} onClick={linkHandler(to)}>
          <b>{t.code}</b> {t.name}
        </a>
      </td>
      <td className={`text ${t.dir > 0 ? "up" : "down"}`}>{t.dir > 0 ? "покупка" : "продажа"}</td>
      <td className="text">
        {fmt.date(t.entryDate)} · {fmt.price(t.entryPrice)}
      </td>
      <td className="text">
        {t.reentry ? "перевход" : t.report ? `${fmt.date(t.report)} → ${fmt.date(t.published)}` : "—"}
      </td>
      <td>{fmt.price(t.lastClose)}</td>
      <td>
        {fmt.price(t.stop)}
        {t.trailing ? <span className="muted"> · 17 дн.</span> : null}
      </td>
      <td>{usd(t.stopUsd)}</td>
      <td>{usd(t.riskUsd)}</td>
      <td className={t.pnlUsd > 0 ? "up" : t.pnlUsd < 0 ? "down" : undefined}>
        {usd(t.pnlUsd)} <span className="muted">({fmt.signedPct(t.pnlPct)})</span>
      </td>
      <td>{fmt.int(t.days)}</td>
    </tr>
  );
}
